import { useQuery } from '@tanstack/react-query';

import { getMe, ME_QUERY_KEY } from '../api/endpoints/permissions';
import type { AccessProfile, PermissionKey } from '../api/schemas';
import { ALL_PERMISSIONS } from '../features/permissions/catalog';
import { isOwner } from './roles';
import { useSession } from './useSession';

/** How long a teammate's permissions are trusted before the panel asks again. */
const ME_STALE_MS = 30_000;

export type Permissions = {
  can: (key: PermissionKey) => boolean;
  /** False only while a teammate's permissions are still on their way. */
  ready: boolean;
  /** A teammate's profile once known; the owner has none. */
  profile?: AccessProfile;
};

/**
 * What the signed-in person may use. The owner may use everything and never
 * waits; a teammate reads `/v1/business/me`, which the API recomputes on every
 * request, so a switch the owner turns off hides the door on the next refresh.
 * This only hides doors: the API refuses them anyway.
 */
export function usePermissions(): Permissions {
  const { status, session } = useSession();
  const owner = isOwner(session?.role);
  const teammate = status === 'authenticated' && !owner;
  const me = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    enabled: teammate,
    staleTime: ME_STALE_MS,
  });

  if (owner) return { can: () => true, ready: true };
  const granted = me.data?.data.permissions;
  const known = new Set((granted ?? []).filter((key) => ALL_PERMISSIONS.includes(key)));
  const profile = me.data?.data.accessProfile ?? undefined;
  return {
    can: (key) => known.has(key),
    ready: !teammate || granted !== undefined || me.isError,
    ...(profile ? { profile } : {}),
  };
}
